import { localizedStrings } from '@core/strings'
import axios, { AxiosError } from 'axios'
import Constants from '@core/utils/constants'
import { statusCodeType } from '@core/react-query/features/testing'
import { usesNest } from '@core/utils/usesNest'

export class RequestError<T = unknown> extends Error {
  // Present only if the error is an AxiosError (i.e. something that comes from axios)
  axiosError?: AxiosError<T>

  constructor(
    message: string,
    public originalError: AxiosError<T> | Error | null,
    public isNetworkError: boolean,
    private explicitHttpStatus?: number,
  ) {
    super(message)
    this.name = 'RequestError'

    if (originalError instanceof AxiosError) {
      this.axiosError = originalError
    }
  }

  get errorType(): string | undefined {
    return (this.axiosError?.response?.data as any)?.errorType
  }

  get httpStatus(): number | undefined {
    return this.explicitHttpStatus ?? this.axiosError?.response?.status
  }

  /** Consecutive rejections after which a submission stops auto-retrying. */
  static readonly MAX_SYNC_REJECTIONS = 5

  /** An answered 4xx. Network failures and 5xx are transient and never count
   * toward the retry cap. */
  static isRejected(error: unknown): boolean {
    if (!(error instanceof RequestError)) {
      return false
    }
    return (
      !error.isNetworkError &&
      error.httpStatus != null &&
      error.httpStatus >= 400 &&
      error.httpStatus < 500
    )
  }

  /** Past the cap auto-retries stop; a manual sync still works. */
  static isRetryLimitReached(rejectionCount: number | undefined): boolean {
    return (rejectionCount ?? 0) >= RequestError.MAX_SYNC_REJECTIONS
  }

  /** JSON-safe subset for persistence — Error props are non-enumerable and the
   * axios objects are circular + carry auth headers. */
  toPlain() {
    return {
      message: this.message,
      isNetworkError: this.isNetworkError,
      httpStatus: this.httpStatus,
    }
  }
}

export type RequestErrorType<T = unknown> = RequestError<T>

/** What survives a storage round-trip of a RequestError (see toPlain). */
export type PlainRequestError = ReturnType<RequestError['toPlain']>

export const isRequestError = (error: unknown): error is RequestError => {
  return error instanceof RequestError
}

const APIAxiosInstance = axios.create({
  baseURL: Project.api,
  timeout: 60000,
})

APIAxiosInstance.interceptors.request.use(async (config) => {
  config.withCredentials = false

  if (Constants.INTEGRATION_TEST) {
    return config
  }
  // Add Cognito auth header to all requests
  const session = await API.auth.Cognito.getSession()
  if (session && session.accessToken) {
    config.headers.AUTHORIZATION = `Bearer ${session.accessToken.jwtToken}`
  }
  // Add client header: web | android | iOS
  config.headers['siteassist-client'] = API.platform
  // Add E2E test header if the app is running in E2E mode
  if (Constants.E2E) {
    config.headers['E2E-Test'] = '1'
  }

  if (usesNest(config.url)) {
    config.baseURL = Project.nestApi
  }

  return config
})

APIAxiosInstance.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response
  },
  // This is the first axios error interceptor, so error is always an instance of AxiosError
  async (error: AxiosError) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error

    // 427: the backend de-duped a request with a matching X-IDEMPOTENCY-KEY — the
    // original already processed, so treat it as success. The body is empty; callers
    // needing the created entity must refetch.
    if (error.response && error.response.status === 427) {
      return error.response
    }

    if (error?.response?.status === 401 && !Constants.INTEGRATION_TEST) {
      API.logout()
    }

    const getErrorMessage = (errorStatus: statusCodeType) => {
      const data = error?.response?.data as any

      // Extract message
      let responseMessage: string | null
      if (typeof data === 'string') {
        responseMessage = data
      } else if (data?.message) {
        responseMessage = data.message
      } else if (data === null || data === undefined) {
        // null/undefined should fall back to error.message
        responseMessage = null
      } else {
        responseMessage = JSON.stringify(data)
      }

      const isErrorMessageDetailed = (
        errorStatus: statusCodeType,
        defaultMessage: string,
      ) =>
        responseMessage === `${errorStatus} Error` || !responseMessage
          ? defaultMessage
          : responseMessage

      let message: string
      switch (errorStatus) {
        case 400:
        case 401:
        case 500:
          message = responseMessage || error.message
          break
        case 404:
          message = isErrorMessageDetailed(
            errorStatus,
            localizedStrings.notFound,
          )
          break
        case 403:
          message = isErrorMessageDetailed(
            errorStatus,
            localizedStrings.unauthorized,
          )
          break
        default:
          message = localizedStrings.defaultErrorMessage
      }

      return message
    }

    const isNetworkError =
      error.code === AxiosError.ERR_NETWORK ||
      ['Network Error', 'timeout exceeded'].includes(error.message)

    const errorMessage = isNetworkError
      ? error.message
      : getErrorMessage(error?.response?.status as statusCodeType)

    return Promise.reject(new RequestError(errorMessage, error, isNetworkError))
  },
)

// Used for anonymous requests that are authenticated using x-access-token instead of the Authorization header
// Example: Checklist Briefings
export const headersWithAccessToken = (args: { accessToken?: string }) => {
  const headers: any = {}
  if (args?.accessToken) {
    headers['X-ACCESS-TOKEN'] = args.accessToken
  }
  return headers
}

export { APIAxiosInstance }
